import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { trpc } from "../../app/trpc.js";
import { Card, Chip, Input, Label, Notice, PageTitle, Spinner } from "../../ui/index.js";
import { addDays, dayKey, dayStart, fmtDay, fmtMoney, useCabinet } from "../cabinet/index.js";

type Preset = "this" | "last" | "custom";

const monthStart = (key: string) => `${key.slice(0, 7)}-01`;
const shiftMonth = (key: string, n: number) => {
  const [y, m] = key.split("-").map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
};

/**
 * Отчёт владельцу за любой период: диалоги, записи бота и мастеров, передачи, отмены, неявки.
 * На широком экране сводка бота слева, остальные плитки сеткой справа.
 * Ждём пояс салона: «сегодня» считается в нём и запоминается в состоянии.
 */
export function ReportsPage() {
  const cab = useCabinet();
  if (cab.loading) return <Spinner />;
  return <ReportsPageView />;
}

function ReportsPageView() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const cab = useCabinet();
  const [params] = useSearchParams();
  const today = dayKey(new Date(), cab.timezone);
  const fromMonth = params.get("month");
  const [preset, setPreset] = useState<Preset>(fromMonth ? "custom" : "this");
  const [from, setFrom] = useState(fromMonth ? `${fromMonth}-01` : monthStart(today));
  const [to, setTo] = useState(fromMonth ? addDays(shiftMonth(`${fromMonth}-01`, 1), -1) : today);

  const choose = (p: Preset) => {
    setPreset(p);
    if (p === "this") {
      setFrom(monthStart(today));
      setTo(today);
    } else if (p === "last") {
      const start = shiftMonth(today, -1);
      setFrom(start);
      setTo(addDays(monthStart(today), -1));
    }
  };
  const range = useMemo(() => ({ from: dayStart(from, cab.timezone), to: dayStart(addDays(to, 1), cab.timezone) }), [from, to, cab.timezone]);
  const valid = from <= to;
  const report = useQuery(trpc.cabinet.reports.period.queryOptions({ salonId: cab.salonId, ...range }, { enabled: valid && cab.isOwner }));

  if (cab.loading) return <Spinner />;
  if (!cab.isOwner) return <Notice>{t("reports.ownerOnly")}</Notice>;
  const r = report.data;
  return (
    <div>
      <PageTitle>{t("reports.title")}</PageTitle>
      <div className="mb-3 flex gap-2 overflow-x-auto lg:flex-wrap">
        {(["this", "last", "custom"] as const).map((p) => (
          <Chip key={p} active={preset === p} onClick={() => choose(p)}>
            {t(`reports.preset.${p}`)}
          </Chip>
        ))}
      </div>
      {preset === "custom" && (
        <div className="mb-3 flex gap-2 lg:max-w-md">
          <div className="flex-1">
            <Label htmlFor="r-from">{t("cal.fromDay")}</Label>
            <Input id="r-from" type="date" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} />
          </div>
          <div className="flex-1">
            <Label htmlFor="r-to">{t("cal.toDay")}</Label>
            <Input id="r-to" type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} />
          </div>
        </div>
      )}
      <p className="mb-3 text-sm text-slate-500">
        {fmtDay(from, lang, { day: "numeric", month: "long" })} – {fmtDay(to, lang, { day: "numeric", month: "long", year: "numeric" })}
      </p>
      {report.isPending && valid && <Spinner />}
      {report.isError && <Notice kind="error">{t("common.error")}</Notice>}
      {r && (
        <div className="space-y-3 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-4 lg:space-y-0">
          <Card className="bg-slate-900 text-white lg:flex lg:flex-col lg:justify-center lg:p-6">
            <div className="text-sm opacity-70">{t("reports.botBooked")}</div>
            <div className="text-3xl font-semibold lg:text-5xl">{r.bookings.bot.count}</div>
            <div className="opacity-80">{fmtMoney(r.bookings.bot.revenueRsd, lang)}</div>
          </Card>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 lg:gap-4">
            <Tile label={t("reports.conversations")} value={r.conversations} />
            <Tile label={t("reports.handoffs")} value={r.handoffs} />
            <Tile label={t("reports.masterBooked")} value={r.bookings.master.count} hint={fmtMoney(r.bookings.master.revenueRsd, lang)} />
            <Tile label={t("reports.pageBooked")} value={r.bookings.booking_page.count} hint={fmtMoney(r.bookings.booking_page.revenueRsd, lang)} />
            <Tile label={t("reports.cancelled")} value={r.cancelled} />
            <Tile label={t("reports.noShows")} value={r.noShows} />
          </div>
          <p className="text-xs text-slate-500 lg:col-span-2">{t("reports.note")}</p>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <Card>
      <div className="text-sm text-slate-500">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
      {hint && <div className="text-sm text-slate-500">{hint}</div>}
    </Card>
  );
}
